/**
 * EmptyState: placeholder dashed cuando una colección está vacía.
 *
 * Sprint 14 S14-06c. Replica `.empty` del mock `docs/dev/ideas/mis-datos.html`
 * (líneas 583-591). NO está en el handoff canónico todavía
 * (`design-system/components.css`) porque es un patrón específico de la
 * página Mis Datos rediseñada (Bloque C del Sprint 14).
 *
 * Visual: card dashed con título + descripción + slot opcional de acción.
 * Pensado para reemplazar la lista cuando no hay items (vs `AddCard` que se
 * suma al final de una lista que sí tiene items).
 *
 * Uso:
 *   <EmptyState
 *     title="Aún no has agregado residentes"
 *     description="Registra a las personas que viven contigo para que reciban los avisos del barrio."
 *     action={
 *       <Button variant="primary" onClick={() => setOpen(true)}>
 *         + Agregar el primer residente
 *       </Button>
 *     }
 *   />
 *
 * Para EmptyState sin acción (caso read-only), basta omitir `action`.
 */
import type { ReactNode } from 'react';

interface EmptyStateProps {
  title: ReactNode;
  description?: ReactNode;
  /** Slot opcional para el botón de acción primario. */
  action?: ReactNode;
  className?: string;
}

export function EmptyState({
  title,
  description,
  action,
  className = '',
}: EmptyStateProps) {
  // padding 32px = p-12, border 1px dashed border-line-strong, rounded-md
  return (
    <div
      className={`border border-dashed border-line-strong rounded-md p-12 text-center text-text-secondary ${className}`.trim()}
      role="status"
    >
      <h4 className="m-0 mb-1 text-text-primary font-medium text-lg">{title}</h4>
      {description && (
        <p className="m-0 mb-4 text-text-muted text-md">{description}</p>
      )}
      {action && <div className="flex justify-center">{action}</div>}
    </div>
  );
}
